/**
 * Gamification store for web - XP, levels, badges, and rewards
 * Syncs with backend for cross-device consistency
 * Web version is read-only (displays data from backend)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '../services/api';

// Badge definitions
export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earnedAt?: string;
  requiresAsIs?: boolean;
}

export const BADGES: Record<string, Omit<Badge, 'earnedAt'>> = {
  first_scan: {
    id: 'first_scan',
    name: 'First Scan',
    description: 'Scan your first As-Is item',
    icon: 'scan',
    requiresAsIs: true,
  },
  store_explorer: {
    id: 'store_explorer',
    name: 'Store Explorer',
    description: 'Scan at 3 different stores',
    icon: 'map',
  },
  referral_rookie: {
    id: 'referral_rookie',
    name: 'Referral Rookie',
    description: 'Get your first referral',
    icon: 'user-plus',
  },
  referral_champion: {
    id: 'referral_champion',
    name: 'Referral Champion',
    description: 'Get 10 referrals',
    icon: 'users',
  },
  century_scanner: {
    id: 'century_scanner',
    name: 'Century Scanner',
    description: 'Scan 100 As-Is items',
    icon: 'award',
    requiresAsIs: true,
  },
  daily_dedicated: {
    id: 'daily_dedicated',
    name: 'Daily Dedicated',
    description: 'Use app 7 days in a row',
    icon: 'calendar',
  },
  // Level badges
  first_steps: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Reach Level 1',
    icon: 'footprints',
  },
  deal_seeker: {
    id: 'deal_seeker',
    name: 'Deal Seeker',
    description: 'Reach Level 3',
    icon: 'search',
  },
  bargain_hunter: {
    id: 'bargain_hunter',
    name: 'Bargain Hunter',
    description: 'Reach Level 5',
    icon: 'tag',
  },
  as_is_expert: {
    id: 'as_is_expert',
    name: 'As-Is Expert',
    description: 'Reach Level 7',
    icon: 'star',
  },
  ikea_legend: {
    id: 'ikea_legend',
    name: 'IKEA Legend',
    description: 'Reach Level 9',
    icon: 'trophy',
  },
  grandmaster: {
    id: 'grandmaster',
    name: 'Grandmaster',
    description: 'Reach Level 10',
    icon: 'diamond',
  },
};

// Level thresholds and rewards
export interface LevelInfo {
  level: number;
  xpRequired: number;
  cumulativeXp: number;
  title: string;
  badgeId?: string;
  watchlistSlotReward?: boolean;
  premiumDaysReward?: number;
}

export const LEVELS: LevelInfo[] = [
  { level: 1, xpRequired: 500, cumulativeXp: 500, title: 'First Steps', badgeId: 'first_steps' },
  { level: 2, xpRequired: 1000, cumulativeXp: 1500, title: 'Newcomer', watchlistSlotReward: true },
  { level: 3, xpRequired: 2000, cumulativeXp: 3500, title: 'Deal Seeker', badgeId: 'deal_seeker' },
  { level: 4, xpRequired: 4000, cumulativeXp: 7500, title: 'Regular', watchlistSlotReward: true },
  { level: 5, xpRequired: 8000, cumulativeXp: 15500, title: 'Bargain Hunter', badgeId: 'bargain_hunter', premiumDaysReward: 1 },
  { level: 6, xpRequired: 15000, cumulativeXp: 30500, title: 'Veteran', watchlistSlotReward: true },
  { level: 7, xpRequired: 30000, cumulativeXp: 60500, title: 'As-Is Expert', badgeId: 'as_is_expert' },
  { level: 8, xpRequired: 50000, cumulativeXp: 110500, title: 'Master', watchlistSlotReward: true },
  { level: 9, xpRequired: 100000, cumulativeXp: 210500, title: 'IKEA Legend', badgeId: 'ikea_legend', premiumDaysReward: 1 },
  { level: 10, xpRequired: 200000, cumulativeXp: 410500, title: 'Grandmaster', badgeId: 'grandmaster', watchlistSlotReward: true },
];

// XP values (for display purposes)
export const XP_VALUES = {
  AS_IS_SCAN: 5,
  REFERRAL: 500,
  SHARE_FIRST: 100,
  SHARE_SECOND: 150,
  SHARE_THIRD: 300,
};

// Max bonus watchlist slots from gamification
export const MAX_GAMIFICATION_SLOTS = 10;

interface ShareTracker {
  date: string; // YYYY-MM-DD
  platformsShared: string[];
}

interface GamificationState {
  // Core stats
  xp: number;
  level: number;
  totalAsIsScans: number;
  bonusWatchlistSlots: number;
  badges: Badge[];

  // Streak tracking
  streakDays: number;
  lastActiveDate: string | null;

  // Share tracking (for daily challenges display)
  shareTracker: ShareTracker;

  // Referral tracking
  referralCount: number;

  // Sync state
  lastSyncedAt: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  init: () => Promise<void>;
  syncFromBackend: () => Promise<void>;
  recordShare: (platform: string) => Promise<{ xpAwarded: number; alreadyShared: boolean }>;
  getCurrentLevelInfo: () => LevelInfo | undefined;
  getNextLevelInfo: () => LevelInfo | undefined;
  getXpProgress: () => { current: number; required: number; percentage: number };
  getLevelIcon: () => 'medal' | 'award' | 'star' | 'trophy' | 'diamond';
  reset: () => void;
}

export const useGamificationStore = create<GamificationState>()(
  persist(
    (set, get) => ({
      xp: 0,
      level: 0,
      totalAsIsScans: 0,
      bonusWatchlistSlots: 0,
      badges: [],
      streakDays: 0,
      lastActiveDate: null,
      shareTracker: { date: '', platformsShared: [] },
      referralCount: 0,
      lastSyncedAt: null,
      isLoading: false,
      error: null,

      init: async () => {
        // Sync from backend on init
        await get().syncFromBackend();
      },

      syncFromBackend: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.getGamificationStatus();

          // Convert backend badges to local format
          const badges: Badge[] = (response.badges || []).map((b: { badgeId: string; badgeName: string; earnedAt?: string }) => {
            const badgeDef = BADGES[b.badgeId];
            return {
              id: b.badgeId,
              name: b.badgeName || badgeDef?.name || b.badgeId,
              description: badgeDef?.description || '',
              icon: badgeDef?.icon || 'star',
              earnedAt: b.earnedAt,
              requiresAsIs: badgeDef?.requiresAsIs,
            };
          });

          set({
            xp: response.xp ?? 0,
            level: response.level ?? 0,
            totalAsIsScans: response.totalAsIsScans ?? 0,
            streakDays: response.streakDays ?? 0,
            bonusWatchlistSlots: response.bonusWatchlistSlots ?? 0,
            referralCount: response.referralCount ?? 0,
            badges,
            lastSyncedAt: new Date().toISOString(),
            isLoading: false,
          });
        } catch (error) {
          console.error('[Gamification] Failed to sync from backend:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load gamification data'
          });
        }
      },

      recordShare: async (platform: string) => {
        const state = get();
        const today = new Date().toISOString().split('T')[0];

        // Reset tracker if new day
        let tracker = state.shareTracker;
        if (tracker.date !== today) {
          tracker = { date: today, platformsShared: [] };
        }

        // Check if already shared to this platform today
        if (tracker.platformsShared.includes(platform)) {
          return { xpAwarded: 0, alreadyShared: true };
        }

        // Check daily cap (3 platforms max)
        if (tracker.platformsShared.length >= 3) {
          return { xpAwarded: 0, alreadyShared: false };
        }

        // Calculate XP based on share order
        const shareCount = tracker.platformsShared.length;
        let xpAmount = 0;

        if (shareCount === 0) {
          xpAmount = XP_VALUES.SHARE_FIRST;
        } else if (shareCount === 1) {
          xpAmount = XP_VALUES.SHARE_SECOND;
        } else if (shareCount === 2) {
          xpAmount = XP_VALUES.SHARE_THIRD;
        }

        // Update tracker locally
        tracker.platformsShared.push(platform);
        set({ shareTracker: tracker });

        // Record share with backend
        try {
          await api.recordShare(platform);
          // Re-sync to get updated XP
          await get().syncFromBackend();
        } catch (error) {
          console.error('[Gamification] Failed to record share:', error);
        }

        return { xpAwarded: xpAmount, alreadyShared: false };
      },

      getCurrentLevelInfo: () => {
        const { level } = get();
        return LEVELS.find(l => l.level === level);
      },

      getNextLevelInfo: () => {
        const { level } = get();
        return LEVELS.find(l => l.level === level + 1);
      },

      getXpProgress: () => {
        const { xp, level } = get();
        const currentLevel = LEVELS.find(l => l.level === level);
        const nextLevel = LEVELS.find(l => l.level === level + 1);

        if (!nextLevel) {
          // Max level
          return { current: xp, required: xp, percentage: 100 };
        }

        const currentThreshold = currentLevel?.cumulativeXp || 0;
        const nextThreshold = nextLevel.cumulativeXp;
        const xpIntoLevel = xp - currentThreshold;
        const xpNeeded = nextThreshold - currentThreshold;

        return {
          current: xpIntoLevel,
          required: xpNeeded,
          percentage: Math.min(100, (xpIntoLevel / xpNeeded) * 100),
        };
      },

      getLevelIcon: () => {
        const { level } = get();
        if (level >= 10) return 'diamond';
        if (level >= 7) return 'trophy';
        if (level >= 5) return 'star';
        if (level >= 3) return 'award';
        return 'medal';
      },

      reset: () => {
        set({
          xp: 0,
          level: 0,
          totalAsIsScans: 0,
          bonusWatchlistSlots: 0,
          badges: [],
          streakDays: 0,
          lastActiveDate: null,
          shareTracker: { date: '', platformsShared: [] },
          referralCount: 0,
          lastSyncedAt: null,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'rumio-gamification',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        xp: state.xp,
        level: state.level,
        totalAsIsScans: state.totalAsIsScans,
        bonusWatchlistSlots: state.bonusWatchlistSlots,
        badges: state.badges,
        streakDays: state.streakDays,
        lastActiveDate: state.lastActiveDate,
        shareTracker: state.shareTracker,
        referralCount: state.referralCount,
        lastSyncedAt: state.lastSyncedAt,
      }),
    }
  )
);
